import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import {
  User, Edit, Trash2, Mail, UserPlus, Search, CheckCircle, XCircle, Building,
  ChevronDown, ChevronUp, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
  RefreshCw, Archive, AlertTriangle, X
} from 'lucide-react';
import { UserContext } from '../../contexts/UserContext';
import AddOrEditUserForm from './UserCreator';

const API_BASE_URL = `${process.env.REACT_APP_API_URL.replace(/\/$/, '')}/api/users`;

const Users = () => {
  const { user } = useContext(UserContext);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCriteria, setFilterCriteria] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState('username');
  const [sortDirection, setSortDirection] = useState('asc');
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [bulkSyncLoading, setBulkSyncLoading] = useState(false);
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [userToArchive, setUserToArchive] = useState(null);
  const [archiving, setArchiving] = useState(false);

  const handleCreateUser = () => {
    setSelectedUserId(null);
    setIsFormVisible(true);
  };

  const handleEditUser = (userId) => {
    setSelectedUserId(userId);
    setIsFormVisible(true);
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    filterAndSortUsers();
  }, [users, searchTerm, filterCriteria, sortField, sortDirection]);

  // Reset to first page when items per page changes or when filtered results change
  useEffect(() => {
    const calculatedTotalPages = Math.ceil(filteredUsers.length / itemsPerPage) || 1;
    if (currentPage > calculatedTotalPages && calculatedTotalPages > 0) {
      setCurrentPage(1);
    }
  }, [itemsPerPage, filteredUsers.length, currentPage]);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API_BASE_URL}/?fields=minimal`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(response.data);
      setFilteredUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError(error.response?.data?.detail || 'Failed to fetch users. Please try again.');
    }
  };

  const handleSort = (field) => {
    const newDirection = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(newDirection);
  };

  const filterAndSortUsers = () => {
    let result = [...users];

    // Apply search filter
    if (searchTerm.trim() !== '') {
      const lowerCaseSearchTerm = searchTerm.toLowerCase();
      result = result.filter(user =>
        (user.username || '').toLowerCase().includes(lowerCaseSearchTerm) ||
        (user.company_email || '').toLowerCase().includes(lowerCaseSearchTerm) ||
        `${user.first_name || ''} ${user.last_name || ''}`.toLowerCase().includes(lowerCaseSearchTerm)
      );
    }

    // Apply status filter
    if (filterCriteria !== 'all') {
      result = result.filter(user => user[filterCriteria]);
    }

    // Apply sorting
    result.sort((a, b) => {
      let compareA, compareB;

      switch (sortField) {
        case 'name':
          compareA = `${a.first_name || ''} ${a.last_name || ''}`.toLowerCase();
          compareB = `${b.first_name || ''} ${b.last_name || ''}`.toLowerCase();
          break;
        case 'phone_number':
          compareA = (a.phone_number || '').toLowerCase();
          compareB = (b.phone_number || '').toLowerCase();
          break;
        case 'winda_id':
          compareA = (a.winda_id || '').toLowerCase();
          compareB = (b.winda_id || '').toLowerCase();
          break;
        case 'winda_last_sync':
          compareA = a.winda_last_sync || '';
          compareB = b.winda_last_sync || '';
          break;
        default:
          compareA = (a[sortField] || '').toString().toLowerCase();
          compareB = (b[sortField] || '').toString().toLowerCase();
      }

      if (sortDirection === 'asc') {
        return compareA.localeCompare(compareB);
      }
      return compareB.localeCompare(compareA);
    });

    setFilteredUsers(result);
  };

  const handleArchiveClick = (userToRemove) => {
    // Check if user is company admin
    if (userToRemove.is_company_admin) {
      setError('Cannot archive a company admin.');
      setTimeout(() => setError(null), 3000);
      return;
    }
    setUserToArchive(userToRemove);
    setArchiveModalOpen(true);
  };

  const handleConfirmArchive = async () => {
    if (!userToArchive) return;

    setArchiving(true);
    const token = localStorage.getItem('access_token');

    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL.replace(/\/$/, '')}/api/employee-archives/create-archive/`,
        { user_id: userToArchive.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Remove user from local state
      setUsers(users.filter(u => u.id !== userToArchive.id));
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      setArchiveModalOpen(false);
      setUserToArchive(null);
    } catch (error) {
      console.error('Archive error:', error);
      setError(error.response?.data?.error || error.response?.data?.user_id?.[0] || 'Failed to archive user. Please try again.');
      setTimeout(() => setError(null), 5000);
    } finally {
      setArchiving(false);
    }
  };

  const handleToggleActive = async (id, currentStatus) => {
    const token = localStorage.getItem('access_token');
    try {
      await axios.patch(`${API_BASE_URL}/${id}/`,
        { is_active: !currentStatus },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setUsers(users.map(user => user.id === id ? { ...user, is_active: !currentStatus } : user));
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      // Refresh the user list to update filtered results
      await fetchUsers();
    } catch (error) {
      setError('Failed to update user status. Please try again.');
      setTimeout(() => setError(null), 3000);
    }
  };

  const handleToggleCoordinator = async (id, currentStatus) => {
    const token = localStorage.getItem('access_token');
    try {
      await axios.patch(`${API_BASE_URL}/${id}/`,
        { is_coordinator: !currentStatus },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setUsers(users.map(user => user.id === id ? { ...user, is_coordinator: !currentStatus } : user));
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      await fetchUsers();
    } catch (error) {
      setError('Failed to update coordinator status. Please try again.');
      setTimeout(() => setError(null), 3000);
    }
  };

  // Pagination calculations
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage) || 1;
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ChevronDown className="w-4 h-4 text-slate-400" />;
    return sortDirection === 'asc'
      ? <ChevronUp className="w-4 h-4 text-brand-600" />
      : <ChevronDown className="w-4 h-4 text-brand-600" />;
  };

  const getVisiblePages = () => {
      const maxVisiblePages = 5; // Bieżąca strona + 2 do przodu + 2 do tyłu
      const pages = [];
      const startPage = Math.max(1, currentPage - 2);
      const endPage = Math.min(totalPages, currentPage + 2);

      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
      return pages;
    };

  const handleBulkWindaSync = async () => {
    if (!window.confirm('Czy na pewno chcesz zsynchronizować Winda ID dla wszystkich użytkowników firmy?\n\nAre you sure you want to sync Winda ID for all company users?')) {
      return;
    }

    setBulkSyncLoading(true);
    setError(null);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL.replace(/\/$/, '')}/api/users/bulk-winda-sync/`,
        {},
        {
          headers: { Authorization: `Bearer ${user.accessToken}` }
        }
      );

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);

      // Refresh users list
      fetchUsers();
    } catch (err) {
      console.error('Bulk Winda sync error:', err);
      setError(err.response?.data?.error || 'Failed to sync Winda IDs');
    } finally {
      setBulkSyncLoading(false);
    }
  };

  return (
    <div className="h-screen flex flex-col">
      <div className="flex-shrink-0 p-4">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-slate-900">Company Users</h1>
          <div className="flex items-center gap-2">
            <button
              onClick={handleBulkWindaSync}
              disabled={bulkSyncLoading}
              className="bg-steel-200 text-slate-700 px-4 py-2 rounded-xl text-sm hover:bg-steel-300 transition-colors duration-300 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
              title="Synchronizuj Winda ID dla wszystkich użytkowników"
            >
              <RefreshCw size={16} className={`mr-2 ${bulkSyncLoading ? 'animate-spin' : ''}`} />
              {bulkSyncLoading ? 'Syncing...' : 'Bulk Winda Sync'}
            </button>
            <button
              onClick={handleCreateUser}
              className="bg-brand-600 text-white px-4 py-2 rounded-xl text-sm hover:bg-brand-700 transition-colors duration-300 flex items-center shadow-lg shadow-brand-500/20"
            >
              <UserPlus size={16} className="mr-2" /> Add New User
            </button>
          </div>
        </div>
      </div>
      <div className="flex-1 px-4 pb-4 overflow-hidden">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-steel-100 h-full flex flex-col">
          <div className="p-4 flex-shrink-0">
          <div className="mb-2 flex space-x-4">
            <div className="flex-1 relative">
              <input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-steel-300 rounded-xl shadow-sm focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
              />
              <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
            </div>
            <select
              value={filterCriteria}
              onChange={(e) => setFilterCriteria(e.target.value)}
              className="pr-10 border border-steel-300 rounded-xl shadow-sm focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500 px-4"
            >
              <option value="all">All Users</option>
              <option value="is_active">Active Users</option>
              <option value="is_staff">Staff Users</option>
              <option value="is_company_admin">Company Admins</option>
              <option value="is_coordinator">Coordinators</option>
              <option value="inspector">Inspectors</option>
            </select>
          </div>

          {error && (
            <div className="bg-red-50 text-red-600 p-4 rounded-xl mb-4">
              {error}
            </div>
          )}
          {success && (
            <div className="bg-green-50 text-green-600 p-4 rounded-xl mb-4">
              Operation completed successfully!
            </div>
          )}

          </div>
          
          {/* Table container */}
          <div className="flex-1 overflow-hidden flex flex-col">
            <div className="flex-1 overflow-y-auto">
              <table className="min-w-full divide-y divide-steel-200">
                <thead className="bg-steel-100 border-b-2 border-brand-500 sticky top-0 z-10">
                  <tr>
                    <th className="w-[50px] px-4 py-2 text-left text-xs font-bold text-slate-600 uppercase tracking-wider"></th>
                    <th 
                      className="px-4 py-2 text-left text-xs font-bold text-slate-600 uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSort('username')}
                    >
                      <div className="flex items-center gap-2">
                        Username
                        <SortIcon field="username" />
                      </div>
                    </th>
                    <th 
                      className="px-4 py-2 text-left text-xs font-bold text-slate-600 uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSort('name')}
                    >
                      <div className="flex items-center gap-2">
                        Name
                        <SortIcon field="name" />
                      </div>
                    </th>
                    <th
                      className="px-4 py-2 text-left text-xs font-bold text-slate-600 uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSort('company_email')}
                    >
                      <div className="flex items-center gap-2">
                        Company Email
                        <SortIcon field="company_email" />
                      </div>
                    </th>
                    <th
                      className="px-4 py-2 text-left text-xs font-bold text-slate-600 uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSort('phone_number')}
                    >
                      <div className="flex items-center gap-2">
                        Phone Number
                        <SortIcon field="phone_number" />
                      </div>
                    </th>
                    <th
                      className="px-4 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSort('winda_id')}
                    >
                      <div className="flex items-center gap-2">
                        GWO / WINDA
                        <SortIcon field="winda_id" />
                      </div>
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-2 text-center text-xs font-bold text-slate-600 uppercase tracking-wider">Coordinator</th>
                    <th className="px-4 py-2 text-right text-xs font-bold text-slate-600 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-steel-200">
                {paginatedUsers.map((user) => (
                  <tr
                    key={user.id}
                    onDoubleClick={() => handleEditUser(user.id)}
                    className="cursor-pointer hover:bg-brand-50 transition-colors"                    
                    >
                    <td className="px-4 py-2 whitespace-nowrap">
                      {user.profile_picture ? (
                        <img
                          src={user.profile_picture}
                          alt={user.username}
                          className="w-8 h-8 rounded-full object-cover"
                        />
                      ) : (
                        <User className="w-8 h-8 text-slate-400" />
                      )}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap font-medium">{user.username || 'N/A'}</td>
                    <td className="px-4 py-2 whitespace-nowrap">{`${user.first_name || ''} ${user.last_name || ''}`.trim() || 'N/A'}</td>
                    <td className="px-4 py-2 whitespace-nowrap">{user.company_email || 'N/A'}</td>
                    <td className="px-4 py-2 whitespace-nowrap">{user.phone_number || 'N/A'}</td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex flex-col gap-0.5">
                        {user.winda_id ? (
                          <span className="font-mono text-sm bg-green-50 text-green-700 px-1.5 py-0.5 rounded w-fit">{user.winda_id}</span>
                        ) : (
                          <span className="text-slate-400 text-sm">-</span>
                        )}
                        {user.winda_last_sync && (
                          <span className="text-[10px] text-slate-400 pl-1.5">
                            {new Date(user.winda_last_sync).toLocaleDateString('pl-PL', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <div className="flex gap-2 items-center">
                        {/* Toggle Switch */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleActive(user.id, user.is_active);
                          }}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                            user.is_active ? 'bg-green-500 focus:ring-green-500' : 'bg-steel-300 focus:ring-steel-400'
                          }`}
                          title={user.is_active ? 'Active - Click to deactivate' : 'Inactive - Click to activate'}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              user.is_active ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                        {/* Status Text */}
                        <span className={`text-xs font-medium ${user.is_active ? 'text-green-700' : 'text-slate-500'}`}>
                          {user.is_active ? 'Active' : 'Inactive'}
                        </span>
                        {user.is_staff && (
                          <span className="bg-steel-100 text-slate-800 px-2 py-1 rounded-xl text-xs">
                            Staff
                          </span>
                        )}
                        {user.is_company_admin && (
                          <span className="bg-brand-100 text-brand-800 px-2 py-1 rounded-xl text-xs">
                            Admin
                          </span>
                        )}
                        {user.inspector && (
                          <span className="bg-brand-100 text-brand-800 px-2 py-1 rounded-xl text-xs">
                            Inspector
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <div className="flex items-center justify-center">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleCoordinator(user.id, user.is_coordinator);
                          }}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                            user.is_coordinator ? 'bg-brand-500 focus:ring-brand-500' : 'bg-steel-300 focus:ring-steel-400'
                          }`}
                          title={user.is_coordinator ? 'Coordinator - Click to disable' : 'Not Coordinator - Click to enable'}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              user.is_coordinator ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => handleEditUser(user.id)}
                          className="text-yellow-600 hover:text-yellow-700 p-1"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => handleArchiveClick(user)}
                          className="text-red-600 hover:text-red-700 p-1"
                          title="Archive and remove from company"
                        >
                          <Archive size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between mt-6 px-4 pb-4">
            <div className="flex items-center gap-4">
              <div className="text-sm text-slate-600">
                Showing {(currentPage - 1) * itemsPerPage + 1}-
                {Math.min(currentPage * itemsPerPage, filteredUsers.length)} of {filteredUsers.length} items
              </div>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="text-sm px-4 py-2 border border-steel-300 rounded-xl shadow-sm focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500 min-w-[140px]"
              >
                <option value={5}>5 per page</option>
                <option value={10}>10 per page</option>
                <option value={25}>25 per page</option>
                <option value={50}>50 per page</option>
                <option value={100}>100 per page</option>
              </select>
            </div>
            <div className="flex space-x-2">
              {currentPage > 1 && (
                <button
                  className="px-4 py-2 border border-steel-300 text-slate-700 rounded-xl hover:bg-brand-50 flex items-center justify-center transition-colors"
                  onClick={() => setCurrentPage(currentPage - 1)}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-chevron-left h-4 w-4 mr-1"
                  >
                    <path d="m15 18-6-6 6-6" />
                  </svg>
                  Previous
                </button>
              )}
              {getVisiblePages().map((page) => (
                <button
                  key={page}
                  className={`px-4 py-2 border ${
                    page === currentPage
                      ? 'border-brand-500 bg-brand-50 text-brand-700'
                      : 'border-steel-300 text-slate-700'
                  } rounded-xl hover:bg-brand-50 flex items-center justify-center transition-colors`}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </button>
              ))}
              {currentPage < totalPages && (
                <button
                  className="px-4 py-2 border border-steel-300 text-slate-700 rounded-xl hover:bg-brand-50 flex items-center justify-center transition-colors"
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  Next
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-chevron-right h-4 w-4 ml-1"
                  >
                    <path d="m9 18 6-6-6-6" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {isFormVisible && (
            <AddOrEditUserForm
              isCreatorOpen={isFormVisible}
              setIsCreatorOpen={setIsFormVisible}
              editingUserId={selectedUserId}
              onSave={fetchUsers}
            />
          )}
        </div>
      </div>

      {/* Archive Confirmation Modal */}
      {archiveModalOpen && userToArchive && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
            <div className="bg-red-50 px-6 py-4 flex items-center gap-3 border-b border-red-100">
              <div className="bg-red-100 rounded-full p-2">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900">Archive Employee</h3>
              <button
                onClick={() => {
                  setArchiveModalOpen(false);
                  setUserToArchive(null);
                }}
                className="ml-auto text-slate-400 hover:text-slate-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              <p className="text-slate-700 mb-4">
                Are you sure you want to archive <span className="font-semibold">{userToArchive.first_name} {userToArchive.last_name}</span>?
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-amber-800 font-medium mb-2">This action will:</p>
                <ul className="text-sm text-amber-700 space-y-1 list-disc list-inside">
                  <li>Create an archive of all employee data</li>
                  <li>Copy all associated files (certificates, nominations, etc.)</li>
                  <li>Remove the employee from your company</li>
                  <li>Clear their company email address</li>
                </ul>
              </div>
              <p className="text-sm text-slate-500">
                The archive will be retained for 2 years as required by law. The employee can still log in with their personal email if they have one.
              </p>
            </div>
            <div className="px-6 py-4 bg-slate-50 flex justify-end gap-3 border-t border-slate-200">
              <button
                onClick={() => {
                  setArchiveModalOpen(false);
                  setUserToArchive(null);
                }}
                className="px-4 py-2 text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                disabled={archiving}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmArchive}
                disabled={archiving}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {archiving ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Archiving...
                  </>
                ) : (
                  <>
                    <Archive className="w-4 h-4" />
                    Archive Employee
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;